import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, Eye, EyeOff, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing or invalid reset link");
      return;
    }
    if (!password) {
      toast.error("Please enter a new password");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can now log in.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <Card className="w-full max-w-md card-official">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-royal-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
            {done ? <CheckCircle2 className="w-8 h-8 text-green-600" /> : <KeyRound className="w-8 h-8 text-royal-purple" />}
          </div>
          <CardTitle className="heading-card text-royal-blue">{done ? "Password Updated" : "Set a New Password"}</CardTitle>
          <CardDescription>
            {done ? "Your password has been changed successfully." : "Choose a new password for your account."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!done ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="form-label">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input pl-10 pr-10"
                    placeholder="New password"
                    data-testid="reset-password-input"
                    style={{ fontSize: "16px" }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Min 8 characters, 1 uppercase &amp; 1 special character.
                </p>
              </div>
              <Button type="submit" className="w-full btn-gold" disabled={loading} data-testid="reset-submit-btn">
                {loading ? "Updating…" : "Update Password"}
              </Button>
            </form>
          ) : (
            <Button className="w-full btn-gold" onClick={() => navigate("/subscribers/login")} data-testid="reset-go-login-btn">
              Go to Login
            </Button>
          )}

          <div className="mt-6 pt-6 border-t text-center">
            <Link to="/forgot-password" className="text-sm text-royal-blue hover:underline">
              Request a new reset link
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
